import { SportType, Team } from '@/types';

type FlagEntry = {
  flag: string;
  aliases: string[];
};

const FLAG_ENGLAND = '\u{1F3F4}\u{E0067}\u{E0062}\u{E0065}\u{E006E}\u{E0067}\u{E007F}';
const FLAG_SCOTLAND = '\u{1F3F4}\u{E0067}\u{E0062}\u{E0073}\u{E0063}\u{E0074}\u{E007F}';

const normalizeTeamFlagKey = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

export const nationalTeamFlagEntries: ReadonlyArray<FlagEntry> = [
  { flag: '🇩🇿', aliases: ['778', 'ALG', 'Algeria'] },
  { flag: '🇦🇷', aliases: ['762', 'ARG', 'Argentina'] },
  { flag: '🇦🇺', aliases: ['779', 'AUS', 'Australia'] },
  { flag: '🇦🇹', aliases: ['816', 'AUT', 'Austria'] },
  { flag: '🇧🇪', aliases: ['805', 'BEL', 'Belgium'] },
  { flag: '🇧🇦', aliases: ['1060', 'BIH', 'Bosnia-Herzegovina', 'Bosnia and Herzegovina', 'Bosnia & Herzegovina'] },
  { flag: '🇧🇷', aliases: ['764', 'BRA', 'Brazil'] },
  { flag: '🇨🇦', aliases: ['828', 'CAN', 'Canada'] },
  { flag: '🇨🇻', aliases: ['1930', 'CPV', 'Cape Verde Islands', 'Cape Verde', 'Cabo Verde'] },
  { flag: '🇨🇴', aliases: ['818', 'COL', 'Colombia'] },
  { flag: '🇨🇩', aliases: ['1934', 'COD', 'Congo DR', 'DR Congo', 'Democratic Republic of the Congo', 'Democratic Republic of Congo', 'Congo-Kinshasa'] },
  { flag: '🇭🇷', aliases: ['799', 'CRO', 'Croatia'] },
  { flag: '🇨🇼', aliases: ['9460', 'CUW', 'Curaçao', 'Curacao'] },
  { flag: '🇨🇿', aliases: ['798', 'CZE', 'Czechia', 'Czech Republic'] },
  { flag: FLAG_ENGLAND, aliases: ['770', 'ENG', 'England'] },
  { flag: '🇪🇨', aliases: ['791', 'ECU', 'Ecuador'] },
  { flag: '🇪🇬', aliases: ['825', 'EGY', 'Egypt'] },
  { flag: '🇫🇷', aliases: ['773', 'FRA', 'France'] },
  { flag: '🇩🇪', aliases: ['759', 'GER', 'Germany'] },
  { flag: '🇬🇭', aliases: ['763', 'GHA', 'Ghana'] },
  { flag: '🇭🇹', aliases: ['836', 'HAI', 'Haiti'] },
  { flag: '🇮🇷', aliases: ['840', 'IRN', 'Iran', 'IR Iran', 'Iran IR'] },
  { flag: '🇮🇶', aliases: ['8062', 'IRQ', 'Iraq'] },
  { flag: '🇨🇮', aliases: ['1935', 'CIV', 'Ivory Coast', "Côte d'Ivoire", "Cote d'Ivoire"] },
  { flag: '🇯🇵', aliases: ['766', 'JPN', 'Japan'] },
  { flag: '🇯🇴', aliases: ['8049', 'JOR', 'Jordan'] },
  { flag: '🇰🇷', aliases: ['772', 'KOR', 'South Korea', 'Korea Republic', 'Republic of Korea'] },
  { flag: '🇲🇦', aliases: ['815', 'MAR', 'Morocco'] },
  { flag: '🇲🇽', aliases: ['769', 'MEX', 'Mexico'] },
  { flag: '🇳🇱', aliases: ['8601', 'NED', 'Netherlands', 'Holland'] },
  { flag: '🇳🇿', aliases: ['783', 'NZL', 'New Zealand'] },
  { flag: '🇳🇴', aliases: ['8872', 'NOR', 'Norway'] },
  { flag: '🇵🇦', aliases: ['1836', 'PAN', 'Panama'] },
  { flag: '🇵🇾', aliases: ['761', 'PAR', 'Paraguay'] },
  { flag: '🇵🇹', aliases: ['765', 'POR', 'Portugal'] },
  { flag: '🇶🇦', aliases: ['8030', 'QAT', 'Qatar'] },
  { flag: '🇸🇦', aliases: ['801', 'KSA', 'Saudi Arabia'] },
  { flag: '🇸🇳', aliases: ['804', 'SEN', 'Senegal'] },
  { flag: FLAG_SCOTLAND, aliases: ['8873', 'SCO', 'Scotland'] },
  { flag: '🇿🇦', aliases: ['774', 'RSA', 'South Africa'] },
  { flag: '🇪🇸', aliases: ['760', 'ESP', 'Spain'] },
  { flag: '🇸🇪', aliases: ['792', 'SWE', 'Sweden'] },
  { flag: '🇨🇭', aliases: ['788', 'SUI', 'Switzerland'] },
  { flag: '🇹🇳', aliases: ['802', 'TUN', 'Tunisia'] },
  { flag: '🇹🇷', aliases: ['803', 'TUR', 'Turkey', 'Türkiye'] },
  { flag: '🇺🇸', aliases: ['771', 'USA', 'United States', 'United States of America', 'USMNT'] },
  { flag: '🇺🇾', aliases: ['758', 'URY', 'Uruguay'] },
  { flag: '🇺🇿', aliases: ['8070', 'UZB', 'Uzbekistan'] },
];

const nationalTeamFlagsByAlias = new Map<string, string>();

for (const entry of nationalTeamFlagEntries) {
  for (const alias of entry.aliases) {
    nationalTeamFlagsByAlias.set(normalizeTeamFlagKey(alias), entry.flag);
  }
}

export function resolveNationalTeamFlagEmoji(team: Team, sportType: SportType): string | null {
  if (sportType !== 'soccer') {
    return null;
  }

  const candidates = [team.id, team.abbreviation, team.name, team.city];

  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }

    const flag = nationalTeamFlagsByAlias.get(normalizeTeamFlagKey(candidate));
    if (flag) {
      return flag;
    }
  }

  return null;
}
